import { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";

import { IEMedIntegrationCount } from "../../../@types/admin/AdminEMedIntegration";
import { TableSorted } from "../../../components/TableSorted";
import { useEMedIntegrationCounts } from "../../../hooks/queries/admin/eMedIntegration";
import { useModal } from "../../../hooks/useModal";
import { getShortFormattedUtcDate } from "../../../utils/dateFormatter";
import {
  ErrorMessage,
  FiltersContainer,
  PageContainer,
  PageHeader,
  PageTitle,
  SearchInput,
  TableContainer,
  TableLoadingState,
  TablePagination,
  TableWrapper,
  useMockPagination,
} from "../shared";
import {
  EMED_LAB_RESULT_PROCESS,
  EMED_MEDICATION_PROCESS,
  EMedDrillDownModal,
} from "./EMedDrillDownModal";

const SEARCH_FIELDS: (keyof IEMedIntegrationCount)[] = [
  "date",
  "processName",
  "hospitalName",
];

/** The feed a summary row belongs to, in words. */
function processLabel(processName: string): string {
  if (processName === EMED_LAB_RESULT_PROCESS) return "Lab results";
  if (processName === EMED_MEDICATION_PROCESS) return "Medications";
  return processName;
}

/**
 * eMED Beaumont integration admin page (EN-5160), migrated from the Retool
 * EMedBeaumontIntegration app. Read-only: message counts by date and process,
 * drilling into one date's lab results or medications.
 *
 * Retool split the drill-down across four modal frames (two top-level, two
 * expanded-row tables) selected by the row's `processName`. The portal has one
 * global modal slot, so this opens a single `EMedDrillDownModal` that walks
 * its own levels — see the EN-5149 rule in retool-migration.md. Tabs were the
 * ticket's alternative and were not taken: a row is either a lab-result row or
 * a medication row, so one tab would always be empty.
 */
export function AdminEMedBeaumont() {
  document.title = "eMED Beaumont integration - Admin";

  const { openModal } = useModal();
  const { integrationCounts, isLoading, isError } = useEMedIntegrationCounts();

  const {
    items,
    allItems,
    pagination,
    searchTerm,
    setSearchTerm,
    setPage,
    setPageSize,
  } = useMockPagination({
    data: integrationCounts,
    searchFields: SEARCH_FIELDS,
  });

  const handleRowClick = (row: IEMedIntegrationCount) => {
    openModal(<EMedDrillDownModal row={row} />, {
      width: "min(1400px, 96vw)",
      maxHeight: "90vh",
      showCloseButton: true,
    });
  };

  const columns: ColumnDef<IEMedIntegrationCount, string>[] = useMemo(
    () => [
      {
        accessorKey: "date",
        header: "Date",
        // The API sends a calendar date at UTC midnight; formatting it in the
        // viewer's local zone would show the previous day west of UTC.
        cell: ({ row }) => (
          <span>{getShortFormattedUtcDate(row.original.date)}</span>
        ),
        enableSorting: true,
      },
      {
        // Which feed the row counts. Not a status — the raw values are
        // EMED-LAB-RESULT / EMED-MEDICATION, and neither is a state to badge.
        id: "processName",
        header: "Feed",
        accessorFn: (count) => processLabel(count.processName),
        enableSorting: true,
      },
      {
        accessorKey: "hospitalId",
        header: "Hospital ID",
        cell: ({ row }) => <span>{row.original.hospitalId ?? "-"}</span>,
        enableSorting: true,
      },
      {
        accessorKey: "hospitalName",
        header: "Hospital",
        enableSorting: true,
      },
      {
        // Rendered as text so a genuine zero shows as "0" — TableSorted
        // short-circuits a falsy cell value to "-".
        id: "count",
        header: "Messages",
        accessorFn: (count) => String(count.count ?? 0),
        enableSorting: true,
      },
    ],
    []
  );

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>eMED Beaumont integration</PageTitle>
      </PageHeader>

      <FiltersContainer>
        <SearchInput
          type="text"
          placeholder="Search by date, feed, or hospital..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </FiltersContainer>

      {isError && (
        <ErrorMessage>
          Failed to load the integration counts. Try refreshing the page.
        </ErrorMessage>
      )}

      <TableContainer>
        <TableWrapper refreshKeys={[["admin-emed-integration-counts"]]}>
          <TableSorted
            columns={columns}
            data={items}
            onRowClick={handleRowClick}
            emptyStateText={isLoading ? <TableLoadingState /> : "No rows found"}
          />
        </TableWrapper>
        <TablePagination
          exportCsv={{
            filename: "emed-beaumont",
            columns,
            rows: allItems,
          }}
          pagination={pagination}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </TableContainer>
    </PageContainer>
  );
}
